import { ChangeEvent, FC, useState } from 'react'
import { useForm } from 'react-hook-form'

import CONFIG from '../../../config'
import { FormResponseStatus, IGender } from '../../models'
import { Button, FormFieldLoading, FormLabel, FormLabelError, Input, Radio, Select, Text, Textarea } from '../UI'
import { useControlPrevention } from '../../hooks'
import { controlPreventionService } from '../../services'
import { ControlPreventionItem } from '../Dashboard'

import style from './AdminControlPreventionForm.module.css';

type AdminControlPreventionFormProps = {
  type?: 'CREATE' | 'EDIT',
  article?: any,
  onSubmitChange(respStatus: FormResponseStatus, message?: string): void
}

interface IControlPreventionRegisterForm {
  title: string,
  fromAge: string,
  toAge: string,
  status: string,
  genderId: string,
  description: string,
  templateId: string,
  //html: string,
  orderControl: string,

  // fields for the new html as json
  postWidth: 'half' | 'full',
  postTitle: string,
  postText: string,
  postImageUrl: string | null,
  postImagePosition: 'top' | 'left',
  postButtonText: string,
  postUrl: string
}

const AdminControlPreventionForm:FC<AdminControlPreventionFormProps> = ({ type = 'CREATE', article, onSubmitChange }) => {
  const [tabDesign, setTabDesign] = useState<'preview'|'edit'>('edit')

  let postContentJson: any = {};
  try {
    if (article?.html) {
      postContentJson = JSON.parse(article.html);
    }
  } catch (error) {
    console.log('Error parsing html', error);
    postContentJson = {
      postWidth: 'half',
      postTitle: 'Titulo de la nota',
      postText: 'Texto de la nota',
      postImageUrl: null,
      postImagePosition: 'top',
      postButtonText: 'Leer nota completa',
      postUrl: '#'
    };
  }

  const defaultValues: IControlPreventionRegisterForm = {
    title: (article && article.title ? article.title : ''),
    fromAge: (article && article.from_age ? article.from_age : ''),
    toAge: (article && article.to_age ? article.to_age : ''),
    status: (article ? article?.status : 'true'),
    genderId: (article ? String(article?.gender_id) : ''),
    description: (article && article.description ? article.description : ''),
    templateId: '',
    //html: (article && article.html ? article.html : ''),
    orderControl: (article && article.order_control ? article.order_control : ''),

    // fields for the new html as json
    postWidth: (postContentJson?.postWidth ? postContentJson.postWidth : 'half'),
    postTitle: (postContentJson?.postTitle ? postContentJson.postTitle : 'Titulo de la nota'),
    postText: (postContentJson?.postText ? postContentJson.postText : 'Texto de la nota'),
    postImageUrl: (postContentJson?.postImageUrl ? postContentJson.postImageUrl : null),
    postImagePosition: (postContentJson?.postImagePosition ? postContentJson.postImagePosition : 'top') ,
    postButtonText: (postContentJson?.postButtonText ? postContentJson.postButtonText : 'Leer nota completa'),
    postUrl: (postContentJson?.postUrl ? postContentJson.postUrl : '#')
  }

  const { register, handleSubmit, watch, formState: { errors }, setValue, reset } = useForm<IControlPreventionRegisterForm>({
    defaultValues
  })
  const [submited, setSubmited] = useState<boolean>(false)
  const genders = useControlPrevention('GENDERS')
  const templates = useControlPrevention('TEMPLATES')

  const fieldErrorRemove = (field: any, value: string) => {
    setValue(field, value, {
      shouldValidate: true,
      shouldDirty: true
    })
  }

  const postForm = async (data: any) => {
    let resp: any

    if(type === 'CREATE') resp = await controlPreventionService.postArticle(data)
    else if(type === 'EDIT') resp = await controlPreventionService.updateArticle(data, article.id)

    if(resp && resp.status === 'Ok') {
      if(type === 'CREATE') reset(defaultValues)
      setTimeout(() => onSubmitChange('SUCCESS'), 800)
    } else {
      setTimeout(() => onSubmitChange('ERROR', resp?.message), 800)
    }
    setSubmited(false)
  }

  /*
  const onTemplateChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target
    const template: any = templates.data.find((it: any) => it.id === Number(value))
    setValue('html', (template ? template.tamplate : ''))
  }
  */

  const onSubmit = async (fData: IControlPreventionRegisterForm) => {
    const {
      title,
      fromAge,
      toAge,
      genderId,
      description,
      //html,
      orderControl,
      status,
      postWidth,
      postTitle,
      postText,
      postImageUrl,
      postImagePosition,
      postButtonText,
      postUrl
    } = fData

    setSubmited(true)
    postForm({
      title,
      from_age: fromAge,
      to_age: toAge,
      gender_id: genderId,
      description,
      html: JSON.stringify({
        postWidth,
        postTitle,
        postText,
        postImageUrl,
        postImagePosition,
        postButtonText,
        postUrl
      }),
      order_control: orderControl,
      status
    })
  }

  const onChangeTab = (tab: 'preview'|'edit', event:React.MouseEvent<HTMLButtonElement>) => { 
    event.preventDefault();
    setTabDesign(tab);
  }

  const {postButtonText, postImagePosition, postImageUrl, postText, postTitle, postUrl, postWidth} = watch();

  return (
    <>
      <form noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
        <div className="row">
          <fieldset className="col-5">
            <FormLabel htmlFor="title" required>Título</FormLabel>
            <Input
              {...register('title', { required: 'Digita Título' })}
              id="title"
              name="title"
              placeholder="Título"
              tabIndex={1}
              fullWidth
              error={!!errors.title}
              className="mt-5"
            />
            {errors.title && <FormLabelError>{errors.title?.message}</FormLabelError>}
          </fieldset>

          <fieldset className="col-2">
            <FormLabel htmlFor="fromAge" required>Edad desde</FormLabel>
            <Input
              {...register('fromAge', {
                  required: 'Digita Edad desde',
                  pattern: {
                    value: CONFIG.FORM.PATTERN.NUMBER,
                    message: 'Digita solo números'
                  }
                }
              )}
              id="fromAge"
              name="fromAge"
              placeholder="00"
              tabIndex={2}
              fullWidth
              maxLength={3}
              error={!!errors.fromAge}
              className="mt-5"
            />
            {errors.fromAge && <FormLabelError>{errors.fromAge?.message}</FormLabelError>}
          </fieldset>

          <fieldset className="col-2">
            <FormLabel htmlFor="toAge" required>Edad hasta</FormLabel>
            <Input
              {...register('toAge', {
                  required: 'Digita Edad hasta',
                  pattern: {
                    value: CONFIG.FORM.PATTERN.NUMBER,
                    message: 'Digita solo números'
                  }
                }
              )}
              id="toAge"
              name="toAge"
              placeholder="00"
              tabIndex={3}
              fullWidth
              maxLength={3}
              error={!!errors.toAge}
              className="mt-5"
            />
            {errors.toAge && <FormLabelError>{errors.toAge?.message}</FormLabelError>}
          </fieldset>

          <fieldset className="col-3">
            <FormLabel htmlFor="status" required>Estado</FormLabel>
            <Select
              {...register('status', { required: 'Selecciona Tipo de documento' })}
              id="status"
              name="status"
              defaultValue={defaultValues.status}
              tabIndex={4}
              fullWidth
              required
              error={!!errors.status}
              className="mt-5"
            >
              <option value="true">Habilitado</option>
              <option value="false">Deshabilitado</option>
            </Select>
            {errors.status && <FormLabelError>{errors.status?.message}</FormLabelError>}
          </fieldset>
        </div>

        <div className="row mt-30">
          <fieldset className="col-5">
            <FormLabel htmlFor="description" required>Descripción</FormLabel>
            <Input
              {...register('description', { required: 'Digita Descripción' })}
              id="description"
              name="description"
              placeholder="Descripción"
              tabIndex={5}
              fullWidth
              error={!!errors.description}
              className="mt-5"
            />
            {errors.description && <FormLabelError>{errors.description?.message}</FormLabelError>}
          </fieldset>

          <fieldset className="col-7">
            <FormLabel htmlFor="genderId" required>Género</FormLabel>
            <div className="position-relative mt-5 py-5">
              {genders.data ?
                  <>
                    {genders.data.map((it: IGender, i: number) => {
                      return (
                        <div key={it.id} className={`d-inline-block${i > 0 ? ' ml-20' : ''}`}>
                          <Radio
                            {...register('genderId', { required: 'Selecciona Género' })}
                            id={`genderId${it.name}`}
                            name="genderId"
                            value={it.id}
                            tabIndex={i + 6}
                            error={!!errors.genderId}
                            onChange={() => fieldErrorRemove('genderId', String(it.id))}
                          />
                          <Text component="label" htmlFor={`genderId${it.name}`} size="14" weight="semibold">{it.name}</Text>
                        </div>
                      )
                    })}
                  </>
                :
                  <FormFieldLoading overlay />
              }
            </div>
            {errors.genderId && <FormLabelError>{errors.genderId?.message}</FormLabelError>}
          </fieldset>
        </div>

        <div className="row mt-30">
          {/*}
          <fieldset className="col-5">
            <FormLabel htmlFor="template">Plantilla</FormLabel>
            {templates.data && templates.data.length ?
                <Select
                  {...register('templateId')}
                  id="templateId"
                  name="templateId"
                  defaultValue={defaultValues.templateId}
                  tabIndex={10}
                  fullWidth
                  required
                  error={!!errors.templateId}
                  className="mt-5"
                  onChange={onTemplateChange}
                >
                  <option value="">Selecciona</option>
                  {templates.data.map((it: any) => <option key={it.id} value={it.id}>{it.name}</option>)}
                </Select>
              :
                <Select
                  id="templateId"
                  name="templateId"
                  tabIndex={10}
                  fullWidth
                  required
                  loading={true}
                  error={!!errors.templateId}
                  className="mt-5"
                >
                  <option value="" disabled>Selecciona</option>
                </Select>
            }
            {errors.templateId && <FormLabelError>{errors.templateId?.message}</FormLabelError>}
          </fieldset>
          */}

          <fieldset className="col-2">
            <FormLabel htmlFor="orderControl" required>Orden</FormLabel>
            <Input
              {...register('orderControl', {
                  required: 'Digita Orden',
                  pattern: {
                    value: CONFIG.FORM.PATTERN.NUMBER,
                    message: 'Digita solo números'
                  }
                }
              )}
              id="orderControl"
              name="orderControl"
              placeholder="00"
              tabIndex={11}
              fullWidth
              maxLength={4}
              error={!!errors.orderControl}
              className="mt-5"
            />
            {errors.orderControl && <FormLabelError>{errors.orderControl?.message}</FormLabelError>}
          </fieldset>
        </div>

        {/*
        <div className="row mt-30">
          <fieldset className="col-12">
            <FormLabel htmlFor="html" required>HTML</FormLabel>
            <Textarea
              {...register('html', { required: 'Digita HTML' })}
              id="html"
              name="html"
              tabIndex={12}
              fullWidth
              style={{ minHeight: '170px' }}
              error={!!errors.html}
              className="mt-5"
            />
            {errors.html && <FormLabelError>{errors.html?.message}</FormLabelError>}
          </fieldset>
        </div>
        */}

        {/* ttab for preview and tab for publish */}
        <div className={style.tabControl}>
          <button 
            className={`${style.tabControl__tab} ${tabDesign === 'edit' && style.activeTab}`} 
            onClick={(event) => onChangeTab('edit', event)}
          >
            <img 
              src="/assets/img/icons/edit.svg" 
              alt="Icono editar" 
              className={style.tabControl__icon}
            />
            Editar
          </button>

          <button 
            className={`${style.tabControl__tab} ${tabDesign === 'preview' && style.activeTab}`} 
            onClick={(event) => onChangeTab('preview', event)}
          >
            <img 
              src="/assets/img/icons/password-show.svg" 
              alt="Icono editar" 
              className={style.tabControl__icon}
            />
            Previsualizar
          </button>
        </div>
        
        {
          tabDesign === 'edit' ?
            <>
              <div className="row mt-30">
                <fieldset className="col-12">
                  <FormLabel htmlFor="postWidth" required>Ancho del contenido</FormLabel>
                  <Select
                    {...register('postWidth', { required: 'Selecciona Ancho' })}
                    id="postWidth"
                    name="postWidth"
                    defaultValue={defaultValues.postWidth}
                    tabIndex={12}
                    fullWidth
                    required
                    error={!!errors.postWidth}
                    className="mt-5"
                  >
                    <option value="half">Mitad</option>
                    <option value="full">Completo</option>
                  </Select>
                  {errors.postWidth && <FormLabelError>{errors.postWidth?.message}</FormLabelError>}
                </fieldset>

              </div>

              <div className="row mt-30">
                <fieldset className="col-6">
                  <FormLabel htmlFor="postImageUrl">Imagen</FormLabel>
                  <small> (Dejar vacío para ocultar imagen)</small>
                  <Input
                    {...register('postImageUrl')}
                    id="postImageUrl"
                    name="postImageUrl"
                    placeholder="URL de la imagen"
                    tabIndex={15}
                    fullWidth
                    error={!!errors.postImageUrl}
                    className="mt-5"
                  />
                  {errors.postImageUrl && <FormLabelError>{errors.postImageUrl?.message}</FormLabelError>}
                </fieldset>

                <fieldset className="col-6">
                  <FormLabel htmlFor="postImagePosition" required>Posición de la imagen</FormLabel>
                  <Select
                    {...register('postImagePosition', { required: 'Selecciona Posición' })}
                    id="postImagePosition"
                    name="postImagePosition"
                    defaultValue={defaultValues.postImagePosition}
                    tabIndex={16}
                    fullWidth
                    required
                    error={!!errors.postImagePosition}
                    className="mt-5"
                  >
                    <option value="top">Arriba</option>
                    <option value="left">Izquierda</option>
                  </Select>
                  {errors.postImagePosition && <FormLabelError>{errors.postImagePosition?.message}</FormLabelError>}
                </fieldset>
              </div>

              <div className="row mt-30">
              <fieldset className="col-6">
                  <FormLabel htmlFor="postTitle">Título de la nota</FormLabel>
                  <small> (Dejar vacío para ocultar título)</small>
                  <Input
                    {...register('postTitle')}
                    id="postTitle"
                    name="postTitle"
                    placeholder="Título"
                    tabIndex={13}
                    fullWidth
                    error={!!errors.postTitle}
                    className="mt-5"
                  />
                  {errors.postTitle && <FormLabelError>{errors.postTitle?.message}</FormLabelError>}
                </fieldset>

                <fieldset className="col-6">
                  <FormLabel htmlFor="postText">Texto</FormLabel>
                  <small> (Dejar vacío para ocultar texto)</small>
                  <Textarea
                    {...register('postText')}
                    id="postText"
                    name="postText"
                    placeholder="Texto"
                    tabIndex={14}
                    fullWidth
                    style={{ minHeight: '32px' }}
                    error={!!errors.postText}
                    className="mt-5"
                  />
                  {errors.postText && <FormLabelError>{errors.postText?.message}</FormLabelError>}
                </fieldset>

              </div>

              <div className="row mt-30">
                <fieldset className="col-6">
                  <FormLabel htmlFor="postButtonText">Texto del botón</FormLabel>
                  <small> (Dejar vacío para ocultar botón)</small>
                  <Input
                    {...register('postButtonText')}
                    id="postButtonText"
                    name="postButtonText"
                    placeholder="Texto"
                    tabIndex={17}
                    fullWidth
                    error={!!errors.postButtonText}
                    className="mt-5"
                  />
                  {errors.postButtonText && <FormLabelError>{errors.postButtonText?.message}</FormLabelError>}
                </fieldset>

                <fieldset className="col-6">
                  <FormLabel htmlFor="postUrl" required>URL del botón</FormLabel>
                  <Input
                    {...register('postUrl', { required: 'Digita URL' })}
                    id="postUrl"
                    name="postUrl"
                    placeholder="URL"
                    tabIndex={18}
                    fullWidth
                    error={!!errors.postUrl}
                    className="mt-5"
                  />
                  {errors.postUrl && <FormLabelError>{errors.postUrl?.message}</FormLabelError>}
                </fieldset>
              </div>
            </>
            : <ControlPreventionItem 
                article={{
                  html: JSON.stringify({
                    postWidth,
                    postTitle,
                    postText,
                    postImageUrl,
                    postImagePosition,
                    postButtonText,
                    postUrl
                  })
                }}
                index={0}
                isPreview={true}
              />
        }

        <div className="register-form-btn-wrapper mt-40">
          <Button
            type="submit"
            tabIndex={13}
            fullWidth
            disabled={submited}
            loading={submited}
          >{type === 'CREATE' ? 'CREAR' : 'ACTUALIZAR'}</Button>
        </div>
      </form>
    </>
  )
}

export { AdminControlPreventionForm }